import { useEffect, useMemo, useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../styling/stylingHelper';
import {
  getAICheckerEmailById,
  backprocessAICheckerEmail,
  deepCheckAICheckerEmail,
  reauditAICheckerEmail,
  reprocessAICheckerEmail,
  attachAICheckerMissingFiles,
  fetchAiCheckerEmailHandoffFiles,
  sendIssueToCodeMonkey,
  quantityCheckAICheckerEmail,
  type AICheckerTrace,
  type AICheckerEmailsResult,
  type ConsolidatedDeepCheckResult,
  type DeepCheckMode,
} from '../../../../../services/admin/adminService';
import { Eye } from 'lucide-react';
import { ReviewCardModal } from '../../AuditReview';
import { MsgIdCopyCell } from '../shared/copyCells';
import { deepCheckOutcome } from './deepCheckOutcome';
import { aiCheckerEmailItem } from './buckets';
import { DoneToggle, checkCell, statusBadge } from './rowCells';
import { isQtyFlagOnlyIssue } from './quantityFlags/qtyFlagGrouping';
import { MarkKnownButton } from './knownCases/markKnownButtons';
import {
  DeepCheckButton,
  DeleteOrderButton,
  ReauditButton,
  ReprocessButton,
  RowReloadButton,
} from './rowButtons';
import { RowActionsMenu } from './RowActionsMenu';
import { MixedCheckModal } from './MixedCheckModal';
import { buildWrongProcessingCaseInfo } from './emailPrompt';
import { RowRunChip, acquireRowRunSlot, type RowRun, type RowRunKind, type RowRunsByKind } from './rowRuns';
import type { TFn } from '../types';

type AllEmailsStatusFilter = 'all' | 'order' | 'revenue' | 'notprocessed';
type AllEmailsCheckFilter = 'all' | 'issues' | 'clean' | 'wrong' | 'qtyflags' | 'new_supplier' | 'needs_attention' | 'safe_skip' | 'known';
type AllEmailsWorkFilter = 'all' | 'open' | 'done';

export function AICheckerAllEmailsTable({ data, loading, showAccount, onChanged, focusNonce, t }: { data: AICheckerEmailsResult | null; loading: boolean; showAccount: boolean; onChanged?: () => void; focusNonce?: number; t: TFn }) {
  // Local work-state overrides so "mark done" reflects immediately without a refetch.
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  // Per-row data overrides from a single-row reload (id → fresh trace), applied over the loaded feed.
  const [rowOverrides, setRowOverrides] = useState<Record<string, AICheckerTrace>>({});
  // The email whose full detail card is open in the modal (null = closed).
  const [detail, setDetail] = useState<AICheckerTrace | null>(null);

  // Swap a freshly-reloaded row into the list and drop any stale work-state override for it.
  const handleRowReloaded = (fresh: AICheckerTrace) => {
    setRowOverrides((o) => ({ ...o, [fresh.id]: fresh }));
    setOverrides((o) => { const n = { ...o }; delete n[fresh.id]; return n; });
  };
  // Every long-running row action, keyed by row id then kind. They live here (not in the ⋯ menu) so the
  // menu can close instantly and the outcome stays pinned to the row — several rows can run in parallel
  // and be compared afterwards before marking each done. One run per kind per row.
  const [rowRuns, setRowRuns] = useState<Record<string, RowRunsByKind>>({});
  // The mixed-suppliers result whose detail modal is open (never re-runs the check — the row run keeps it).
  const [mixedDetail, setMixedDetail] = useState<{ trace: AICheckerTrace; result: ConsolidatedDeepCheckResult } | null>(null);

  const setRun = (id: string, kind: RowRunKind, run: RowRun) =>
    setRowRuns((r) => ({ ...r, [id]: { ...r[id], [kind]: run } }));
  const clearRun = (id: string, kind: RowRunKind) =>
    setRowRuns((r) => { const row = { ...r[id] }; delete row[kind]; return { ...r, [id]: row }; });
  const isRunning = (id: string, kind: RowRunKind) => {
    const s = rowRuns[id]?.[kind]?.status;
    return s === 'running' || s === 'queued';
  };

  // Reload just this row (not the whole feed — other runs may still be in flight).
  const reloadRow = async (trace: AICheckerTrace) => {
    try {
      handleRowReloaded(await getAICheckerEmailById(trace.id));
    } catch (err) {
      console.error('Failed to reload audit row after a row action', err);
    }
  };

  /**
   * Run one long action for one row: queue for a slot, show a spinner chip, then a green/red one-liner.
   * Mutating kinds reload their own row afterwards; the deep checks are read-only and reload nothing.
   * Re-firing a kind already in flight on the same row is ignored; other rows are unaffected.
   */
  const runRowAction = async (trace: AICheckerTrace, kind: RowRunKind, platform?: string) => {
    const current = rowRuns[trace.id]?.[kind];
    if (current && (current.status === 'running' || current.status === 'queued')) return;
    // The menu only offers a kind when its id is present; bail before painting a chip we could never finish.
    const needsGmailId = kind === 'backprocess' || kind === 'attachFiles' || kind === 'monkeyWrongProcessing';
    if (needsGmailId ? !trace.gmailMessageId : !trace.rfc822MessageId) return;
    if (kind === 'backprocess' && !platform) return;
    const label = kind === 'backprocess' && platform
      ? `${t('adminTesting.rowRun.label_backprocess')} · ${platform}`
      : t(`adminTesting.rowRun.label_${kind}`);
    setRun(trace.id, kind, { status: 'queued', label });
    const release = await acquireRowRunSlot();
    setRun(trace.id, kind, { status: 'running', label });
    const ok = (msg: string) => setRun(trace.id, kind, { status: 'ok', label, msg });
    const fail = (msg: string) => setRun(trace.id, kind, { status: 'error', label, msg });
    try {
      switch (kind) {
        case 'backprocess': {
          // Backprocess every invoice PDF of this email against the chosen supplier, then run the
          // totals-only deep check so the same click also proves the stored rows reconcile.
          const r = await backprocessAICheckerEmail(trace.account, trace.gmailMessageId as string, platform as string);
          const failed = r.files.filter((f) => f.error).length;
          let summary = t('adminTesting.backprocess.summary', { pdfs: r.pdfCount, orders: r.totalOrders, facturen: r.totalFacturen });
          if (r.quantityFlagCount !== null) summary += ` · ${t('adminTesting.backprocess.quantityFlags', { count: r.quantityFlagCount })}`;
          else summary += ` · ${t('adminTesting.backprocess.checksIncomplete')}`;
          if (failed) summary += ` · ${t('adminTesting.backprocess.filesFailed', { count: failed })}`;
          if (trace.rfc822MessageId) {
            setRun(trace.id, kind, { status: 'running', label, msg: `${summary} · ${t('adminTesting.backprocess.reconciling')}` });
            const dc = await deepCheckAICheckerEmail(trace.account, trace.rfc822MessageId, 'totals');
            const o = deepCheckOutcome(dc, t);
            setRun(trace.id, kind, { status: o.ok && dc.totals?.verdict === 'correct' && !failed && r.quantityFlagCount === 0 ? 'ok' : 'error', label, msg: `${summary} · ${dc.totals ? o.msg : t('adminTesting.backprocess.checksIncomplete')}` });
          } else fail(`${summary} · ${t('adminTesting.backprocess.checksIncomplete')}`);
          break;
        }
        case 'deepAuto':
        case 'deepTotals':
        case 'deepPakbon':
        case 'deepAllFiles': {
          const mode: DeepCheckMode =
            kind === 'deepTotals' ? 'totals'
            : kind === 'deepPakbon' ? 'pakbon'
            : kind === 'deepAllFiles' ? 'backprocessing'
            : 'auto';
          const o = deepCheckOutcome(await deepCheckAICheckerEmail(trace.account, trace.rfc822MessageId as string, mode), t);
          if (o.ok) ok(o.msg); else fail(o.msg);
          break;
        }
        case 'mixed': {
          // Summary on the chip, full per-file breakdown behind a click (the result is kept, not re-run).
          const r = await deepCheckAICheckerEmail(trace.account, trace.rfc822MessageId as string, 'mixed');
          const files = r.mixedFiles || [];
          if (files.length === 0) {
            fail(r.reason || t('adminTesting.review.deepCheckFailed'));
            break;
          }
          const found = files.filter((f) => f.found).length;
          setRun(trace.id, kind, {
            status: found === files.length ? 'ok' : 'error',
            label,
            msg: t('adminTesting.mixedCheck.summary', { found, total: files.length }),
            detail: r,
          });
          break;
        }
        case 'reaudit': {
          const r = await reauditAICheckerEmail(trace.account, trace.rfc822MessageId as string);
          ok(t('adminTesting.review.reauditDone', { found: r.found, audited: r.audited }));
          await reloadRow(trace);
          break;
        }
        case 'addFactuur': {
          // Reprocess with the message_id dedup skipped — the order row exists, the factuur never landed.
          // Green ONLY when factuur_history + factuur_orders are actually there afterwards (the server
          // reads them back): the replay returns normally without writing anything when the sender no
          // longer matches a restaurant_platform, when the email routes to revenue, or when the adapter
          // emits no factuurData — reporting that as success is what made this button look like a no-op.
          const r = await reprocessAICheckerEmail(trace.account, trace.rfc822MessageId as string, true);
          if (r.factuurCreated) {
            ok(t('adminTesting.review.addMissingFactuurDone', { orders: r.factuurOrdersCount }));
          } else {
            const why = r.outcomes[0] ?? 'not_found';
            fail(t('adminTesting.review.addMissingFactuurNothing', {
              reason: t(`adminTesting.review.replayOutcome_${why}`, { defaultValue: why }),
            }));
          }
          await reloadRow(trace);
          break;
        }
        case 'monkeyWrongProcessing': {
          const handoff = await fetchAiCheckerEmailHandoffFiles(trace.account, trace.gmailMessageId as string);
          const rowChecks = Object.values(rowRuns[trace.id] ?? {})
            .filter((r) => r.msg && r.label !== label && (r.status === 'ok' || r.status === 'error'))
            .map((r) => `${r.label}: ${r.msg}`);
          await sendIssueToCodeMonkey({
            flow: 'wrong-processing',
            caseInfo: buildWrongProcessingCaseInfo(trace, handoff, rowChecks),
            context: {
              id: trace.id,
              account: trace.account,
              rfc822MessageId: trace.rfc822MessageId,
              gmailMessageId: trace.gmailMessageId,
              platform: trace.platform,
              subject: trace.emailSubject,
            },
            files: handoff.files.flatMap((f) => (f.dataBase64 ? [{ name: f.filename, dataBase64: f.dataBase64 }] : [])),
          });
          ok(t('adminTesting.review.menuCodeMonkeyWrongProcessingSent', { count: handoff.files.length }));
          break;
        }
        case 'attachFiles': {
          const r = await attachAICheckerMissingFiles({
            account: trace.account || 'all',
            startDate: '2000-01-01',
            endDate: '2100-01-01',
            emailLimit: 1,
            gmailMessageIds: [trace.gmailMessageId as string],
          });
          const d = r.emails[0];
          if (d) {
            // Green only when a file actually landed on a row — "no PDF attachment" is a legitimate
            // outcome (illy/vatex mail their invoice in the body) but it still linked nothing.
            const linked = d.ordersLinked + d.facturenLinked;
            const msg = t('adminTesting.review.missingFilesRowDone', {
              linked,
              outcome: t(`adminTesting.review.missingFilesOutcome_${d.outcome}`),
            });
            if (linked > 0) ok(msg); else fail(msg);
          } else {
            fail(t('adminTesting.review.missingFilesOutcome_email_not_found'));
          }
          await reloadRow(trace);
          break;
        }
      }
    } catch (err: any) {
      fail(err.message || 'Failed');
    } finally {
      // Processing may have stored invoices even if the subsequent totals check failed.
      if (kind === 'backprocess') {
        await reloadRow(trace);
        onChanged?.();
      }
      release();
    }
  };
  // Client-side filters over the loaded feed.
  const [statusFilter, setStatusFilter] = useState<AllEmailsStatusFilter>('all');
  const [checkFilter, setCheckFilter] = useState<AllEmailsCheckFilter>('all');
  const [workFilter, setWorkFilter] = useState<AllEmailsWorkFilter>('all');
  // Refines "Has issue": hide emails whose only problem is open quantity flags.
  const [excludeQtyOnly, setExcludeQtyOnly] = useState(false);
  // Supplier/platform filter — options are the distinct platforms actually present in the loaded feed.
  const [platformFilter, setPlatformFilter] = useState('all');
  const [search, setSearch] = useState('');
  // How many of the filtered rows are actually painted to the DOM. Grows in steps via "Load more"; the
  // feed itself is already loaded in state, so this is purely a render ceiling (no refetch).
  const RENDER_STEP = 100;
  const [renderLimit, setRenderLimit] = useState(200);
  // Targeted reaudit of orders that never got a verdict (emailVerdict === null) — NOT the 'inconclusive'
  // ones (those re-run to the same result). Sequential so we don't hammer the audit pipeline.
  const [reauditing, setReauditing] = useState<{ done: number; total: number } | null>(null);
  // Quantity-only re-check over the CURRENTLY FILTERED rows. Tracks flags found so a long run reports
  // progress as it goes, not just at the end.
  const [qtyCheckFailures, setQtyCheckFailures] = useState(0);
  const [qtyChecking, setQtyChecking] = useState<{ done: number; total: number; flags: number } | null>(null);
  // Reset the ceiling whenever the QUERY changes (new mailbox/range, or a filter narrows/broadens it), so
  // "Load more" always starts fresh instead of dumping the whole list after a filter switch. Deliberately
  // keyed on the query rather than the `data` object: a refetch of the same window (reprocess/reaudit/
  // delete all bump reloadNonce) hands us a new object with the same rows — collapsing back to 200 there
  // would throw away the rows the user just loaded and jump the page back to the top mid-review.
  const queryKey = data ? `${data.account}|${data.startDate}|${data.endDate}` : '';
  useEffect(() => { setRenderLimit(200); }, [queryKey, statusFilter, checkFilter, excludeQtyOnly, workFilter, platformFilter, search]);
  // Distinct platforms in the loaded feed, for the supplier dropdown (unprocessed emails have none).
  const platformOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of data?.emails ?? []) if (e.platform) set.add(e.platform);
    return Array.from(set).sort();
  }, [data]);
  // "Needs attention" shortcut (from the tab row): jump straight to the actionable to-do list —
  // open emails that still have an issue. Bumping focusNonce re-applies the preset.
  useEffect(() => {
    if (!focusNonce) return;
    setStatusFilter('all');
    setCheckFilter('issues');
    setExcludeQtyOnly(false);
    setWorkFilter('open');
    setPlatformFilter('all');
    setSearch('');
  }, [focusNonce]);

  const filtered = useMemo(() => {
    const emails = (data?.emails ?? []).map((e) => rowOverrides[e.id] ?? e);
    const q = search.trim().toLowerCase();
    return emails.filter((e) => {
      const ws = overrides[e.id] ?? e.workState;
      const isDone = ws !== 'open';

      if (statusFilter === 'order' && e.matchedIn !== 'order') return false;
      if (statusFilter === 'revenue' && e.matchedIn !== 'revenue') return false;
      if (statusFilter === 'notprocessed' && e.matchedIn !== null) return false;

      if (platformFilter !== 'all' && e.platform !== platformFilter) return false;

      if (workFilter === 'open' && isDone) return false;
      if (workFilter === 'done' && !isDone) return false;

      if (checkFilter === 'issues' && !e.hasIssue) return false;
      if (checkFilter === 'issues' && excludeQtyOnly && isQtyFlagOnlyIssue(e)) return false;
      if (checkFilter === 'clean' && e.hasIssue) return false;
      if (checkFilter === 'wrong' && e.emailVerdict !== 'wrong') return false;
      if (checkFilter === 'qtyflags' && e.quantityFlagCount <= 0) return false;
      if (checkFilter === 'new_supplier' && e.triageCategory !== 'new_supplier') return false;
      if (checkFilter === 'needs_attention' && e.triageCategory !== 'needs_attention') return false;
      if (checkFilter === 'known' && e.triageCategory !== 'known') return false;
      if (checkFilter === 'safe_skip' && !(e.matchedIn === null && e.triageCategory !== 'new_supplier' && e.triageCategory !== 'needs_attention')) return false;

      if (q) {
        // Make the "Status" + "Check" column content searchable too: verdict, triage, processed state,
        // issues and flags — so "inconclusive", "wrong", "new supplier", "revenue", "qty flags" etc. filter.
        const checkBits = [
          e.emailVerdict ?? (e.matchedIn === 'order' ? 'not audited not checked' : ''),
          e.triageCategory ?? '',
          (e.triageCategory ?? '').replace(/_/g, ' '),
          e.matchedIn ?? 'unprocessed not processed',
          e.hasIssue ? 'issue' : 'clean',
          e.quantityFlagCount > 0 ? 'qty flags quantity' : '',
          e.factuurExpected ? (e.factuurCreated ? 'factuur' : 'factuur missing') : '',
          (e.emailIssues ?? []).join(' '),
        ];
        const hay = [e.emailFrom, e.emailSubject, e.platform, e.restaurantName, e.orderNumbers.join(' '), e.account, ...checkBits].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, overrides, rowOverrides, statusFilter, checkFilter, excludeQtyOnly, workFilter, platformFilter, search]);

  if (loading && !data) {
    return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{t('adminTesting.review.aiCheckerLoading')}</div>;
  }
  if (!data || data.emails.length === 0) {
    return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{t('adminTesting.review.allEmpty')}</div>;
  }
  const fmtDate = (iso: string) => {
    try { return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };
  const filtersActive = statusFilter !== 'all' || checkFilter !== 'all' || workFilter !== 'all' || platformFilter !== 'all' || search.trim() !== '';
  const selectCls = cn('px-2 py-1 rounded-md border text-xs', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText);
  const visible = filtered.slice(0, renderLimit);
  const truncated = filtered.length > renderLimit;

  // Order emails that were processed but never reached a verdict (null, not 'inconclusive') — the only
  // "Not audited" rows a reaudit can actually flip.
  const nullVerdictRows = data.emails.filter((e) => e.matchedIn === 'order' && e.emailVerdict === null && !!e.rfc822MessageId);
  const reauditNull = async () => {
    if (reauditing || nullVerdictRows.length === 0) return;
    if (!window.confirm(t('adminTesting.review.reauditNullConfirm', { count: nullVerdictRows.length }))) return;
    setReauditing({ done: 0, total: nullVerdictRows.length });
    for (let i = 0; i < nullVerdictRows.length; i++) {
      try {
        await reauditAICheckerEmail(nullVerdictRows[i].account, nullVerdictRows[i].rfc822MessageId as string);
      } catch (err) {
        console.error('Failed to reaudit null-verdict email', nullVerdictRows[i].id, err);
      }
      setReauditing({ done: i + 1, total: nullVerdictRows.length });
    }
    setReauditing(null);
    onChanged?.();
  };

  // Re-run ONLY the quantityInfo audit over every filtered order row. The reason this exists: work that
  // stored orders without going through the audit pipeline (notably backprocessing before it audited
  // itself) leaves quantity_flag_count 0, which reads as "clean" but means "never checked". Sequential,
  // like reauditNull — the Gemini audit is the bottleneck and parallel runs just queue anyway.
  const qtyCheckRows = filtered.filter((e) => e.matchedIn === 'order' && !!e.gmailMessageId);
  const quantityCheckFiltered = async () => {
    if (qtyChecking || qtyCheckRows.length === 0) return;
    if (!window.confirm(t('adminTesting.review.qtyCheckConfirm', { count: qtyCheckRows.length }))) return;
    setQtyChecking({ done: 0, total: qtyCheckRows.length, flags: 0 });
    setQtyCheckFailures(0);
    let failures = 0;
    let flags = 0;
    for (let i = 0; i < qtyCheckRows.length; i++) {
      const row = qtyCheckRows[i];
      try {
        const r = await quantityCheckAICheckerEmail(row.account, row.gmailMessageId, row.rfc822MessageId);
        if (r.flagCount === null || !r.updated) failures++;
        else flags += r.flagCount;
      } catch (err) {
        failures++;
        console.error('Failed to quantity-check email', row.id, err);
      }
      setQtyChecking({ done: i + 1, total: qtyCheckRows.length, flags });
    }
    setQtyChecking(null);
    setQtyCheckFailures(failures);
    onChanged?.();
  };

  // One action set for both renderings: the desktop actions cell and the mobile card's action row
  // paint exactly these buttons, so nothing is reachable on desktop only.
  const renderRowActions = (e: AICheckerTrace, workState: string) => (
    <>
      {/* Clean/correctly-processed emails are automatically good — only flagged ones need a "mark done". */}
      {e.hasIssue && (
        <>
          <button
            onClick={() => setDetail(e)}
            title={t('adminTesting.review.viewDetails')}
            className={cn('p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700', secondaryText)}
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <DoneToggle trace={e} workState={workState} onChange={(ws) => setOverrides((o) => ({ ...o, [e.id]: ws }))} t={t} />
          {!e.processed && <MarkKnownButton trace={e} onChanged={onChanged} t={t} />}
          <ReauditButton trace={e} t={t} onChanged={onChanged} />
          <DeepCheckButton trace={e} t={t} onRun={() => runRowAction(e, 'deepAuto')} running={isRunning(e.id, 'deepAuto')} />
          <DeleteOrderButton trace={e} t={t} onChanged={onChanged} />
        </>
      )}
      {/* Inconclusive rows that aren't already in the hasIssue block still get a direct reaudit + deep check. */}
      {!e.hasIssue && e.emailVerdict === 'inconclusive' && (
        <>
          <ReauditButton trace={e} t={t} onChanged={onChanged} />
          <DeepCheckButton trace={e} t={t} onRun={() => runRowAction(e, 'deepAuto')} running={isRunning(e.id, 'deepAuto')} />
        </>
      )}
      <ReprocessButton trace={e} t={t} onChanged={onChanged} />
      <MsgIdCopyCell rfc822MessageId={e.rfc822MessageId} label={e.gmailMessageId} iconOnly />
      <RowActionsMenu
        trace={e}
        onChanged={onChanged}
        onOpenDetail={() => setDetail(e)}
        onRunAction={(kind, platform) => runRowAction(e, kind, platform)}
        isRunning={(kind) => isRunning(e.id, kind)}
        t={t}
      />
    </>
  );

  // Every action started from this row, stacked in start order — shared by both renderings too.
  const renderRowChips = (e: AICheckerTrace) => (
    <>
      {(Object.entries(rowRuns[e.id] ?? {}) as [RowRunKind, RowRun][]).map(([kind, run]) => (
        <RowRunChip
          key={kind}
          run={run}
          onDismiss={() => clearRun(e.id, kind)}
          onOpen={run.detail ? () => setMixedDetail({ trace: e, result: run.detail as ConsolidatedDeepCheckResult }) : undefined}
          t={t}
        />
      ))}
    </>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <select className={selectCls} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as AllEmailsStatusFilter)}>
          <option value="all">{t('adminTesting.review.filterStatusAll')}</option>
          <option value="order">{t('adminTesting.review.statusOrder')}</option>
          <option value="revenue">{t('adminTesting.review.statusRevenue')}</option>
          <option value="notprocessed">{t('adminTesting.review.statusNotProcessed')}</option>
        </select>
        <select className={selectCls} value={checkFilter} onChange={(e) => setCheckFilter(e.target.value as AllEmailsCheckFilter)}>
          <option value="all">{t('adminTesting.review.filterCheckAll')}</option>
          <option value="issues">{t('adminTesting.review.filterCheckIssues')}</option>
          <option value="clean">{t('adminTesting.review.filterCheckClean')}</option>
          <option value="wrong">{t('adminTesting.review.filterCheckWrong')}</option>
          <option value="qtyflags">{t('adminTesting.review.filterCheckQtyFlags')}</option>
          <option value="new_supplier">{t('adminTesting.review.bucketNewSuppliers')}</option>
          <option value="needs_attention">{t('adminTesting.review.bucketNeedsAttention')}</option>
          <option value="known">{t('adminTesting.review.checkKnown')}</option>
          <option value="safe_skip">{t('adminTesting.review.statSafeSkip')}</option>
        </select>
        {checkFilter === 'issues' && (
          <button
            onClick={() => setExcludeQtyOnly((v) => !v)}
            aria-pressed={excludeQtyOnly}
            title={t('adminTesting.review.filterExclQtyFlagsHint')}
            className={cn('px-2 py-1 rounded-md border text-xs transition-colors',
              excludeQtyOnly
                ? 'border-orange-300 dark:border-orange-800 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                : cn('border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600', primaryText))}
          >
            {t('adminTesting.review.filterExclQtyFlags')}
          </button>
        )}
        <select className={selectCls} value={workFilter} onChange={(e) => setWorkFilter(e.target.value as AllEmailsWorkFilter)}>
          <option value="all">{t('adminTesting.review.filterWorkAll')}</option>
          <option value="open">{t('adminTesting.review.filterWorkOpen')}</option>
          <option value="done">{t('adminTesting.review.filterWorkDone')}</option>
        </select>
        {platformOptions.length > 0 && (
          <select className={selectCls} value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
            <option value="all">{t('adminTesting.review.filterPlatformAll')}</option>
            {platformOptions.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        )}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('adminTesting.review.filterSearchPlaceholder')}
          className={cn(selectCls, 'flex-1 min-w-[140px]')}
        />
        {filtersActive && (
          <button
            onClick={() => { setStatusFilter('all'); setCheckFilter('all'); setExcludeQtyOnly(false); setWorkFilter('all'); setPlatformFilter('all'); setSearch(''); }}
            className={cn('px-2 py-1 rounded-md text-xs', 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600')}
          >
            {t('adminTesting.review.filterClear')}
          </button>
        )}
        {nullVerdictRows.length > 0 && (
          <button
            onClick={reauditNull}
            disabled={!!reauditing}
            title={t('adminTesting.review.reauditNullHint')}
            className={cn('px-2 py-1 rounded-md text-xs disabled:opacity-60', 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50')}
          >
            {reauditing
              ? t('adminTesting.review.reauditNullProgress', { done: reauditing.done, total: reauditing.total })
              : t('adminTesting.review.reauditNull', { count: nullVerdictRows.length })}
          </button>
        )}
        {qtyCheckFailures > 0 && <p role="alert" className="text-xs text-amber-700 dark:text-amber-300">{t('adminTesting.review.qtyCheckIncomplete', { count: qtyCheckFailures })}</p>}
        {qtyCheckRows.length > 0 && (
          <button
            onClick={quantityCheckFiltered}
            disabled={!!qtyChecking || !!reauditing}
            title={t('adminTesting.review.qtyCheckHint')}
            className={cn('px-2 py-1 rounded-md text-xs disabled:opacity-60', 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50')}
          >
            {qtyChecking
              ? t('adminTesting.review.qtyCheckProgress', { done: qtyChecking.done, total: qtyChecking.total, flags: qtyChecking.flags })
              : t('adminTesting.review.qtyCheckFiltered', { count: qtyCheckRows.length })}
          </button>
        )}
      </div>
      <div className={cn(secondaryText, 'text-xs mb-2')}>
        {truncated
          ? t('adminTesting.review.allShowingCapped', { shown: visible.length, matching: filtered.length, total: data.total })
          : t('adminTesting.review.allShowingFiltered', { shown: filtered.length, loaded: data.emails.length, total: data.total })}
      </div>
      {/* Desktop/tablet: the full table. Phones get the sm:hidden card list below instead. */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className={cn('border-b border-gray-200 dark:border-gray-700', secondaryText, 'uppercase tracking-wide')}>
              <th className="py-2 pr-2 w-6"></th>
              <th className="py-2 pr-3">{t('adminTesting.review.colDate')}</th>
              {showAccount && <th className="py-2 pr-3">{t('adminTesting.review.colAcct')}</th>}
              <th className="py-2 pr-3">{t('adminTesting.review.labelFrom')}</th>
              <th className="py-2 pr-3">{t('adminTesting.review.labelSubject')}</th>
              <th className="py-2 pr-3">{t('adminTesting.review.labelPlatform')}</th>
              <th className="py-2 pr-3">{t('adminTesting.review.colStatus')}</th>
              <th className="py-2 pr-3">{t('adminTesting.review.colCheck')}</th>
              <th className="py-2 pr-3 text-right">{t('adminTesting.review.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((e) => {
              const workState = overrides[e.id] ?? e.workState;
              const isDone = workState !== 'open';
              return (
                <tr key={e.id} className={cn('border-b border-gray-100 dark:border-gray-800',
                  isDone ? 'opacity-50' : e.hasIssue ? 'bg-red-50/40 dark:bg-red-900/10' : '')}>
                  <td className="py-2 pr-2 align-middle"><RowReloadButton trace={e} onReloaded={handleRowReloaded} t={t} /></td>
                  <td className={cn('py-2 pr-3 whitespace-nowrap', secondaryText)}>{fmtDate(e.emailDate || e.checkedAt)}</td>
                  {showAccount && (
                    <td className="py-2 pr-3">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        {e.account === 'cloud63' ? '63' : e.account === 'cloud64' ? '64' : e.account}
                      </span>
                    </td>
                  )}
                  <td className={cn('py-2 pr-3 max-w-[150px] truncate', secondaryText)} title={e.emailFrom || ''}>{e.emailFrom || '—'}</td>
                  <td className={cn('py-2 pr-3 max-w-[240px] truncate', primaryText)} title={e.emailSubject || ''}>{e.emailSubject || '—'}</td>
                  <td className={cn('py-2 pr-3 font-mono', primaryText)}>{e.platform || '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{statusBadge(e, t)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {checkCell(e, t)}
                    {renderRowChips(e)}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                      {renderRowActions(e, workState)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Phone: one card per email with the SAME fields and the SAME action set as the table row —
          the sideways-scrolling table hid the actions off-screen, which is exactly what this fixes. */}
      <div className="sm:hidden space-y-2">
        {visible.map((e) => {
          const workState = overrides[e.id] ?? e.workState;
          const isDone = workState !== 'open';
          return (
            <div
              key={e.id}
              className={cn('rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2',
                isDone ? 'opacity-50' : e.hasIssue ? 'bg-red-50/40 dark:bg-red-900/10' : 'bg-white dark:bg-gray-800')}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <RowReloadButton trace={e} onReloaded={handleRowReloaded} t={t} />
                  <span className={cn(secondaryText, 'text-[11px] whitespace-nowrap')}>{fmtDate(e.emailDate || e.checkedAt)}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {showAccount && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {e.account === 'cloud63' ? '63' : e.account === 'cloud64' ? '64' : e.account}
                    </span>
                  )}
                  {statusBadge(e, t)}
                </div>
              </div>
              <div className="min-w-0">
                <div className={cn(primaryText, 'text-xs font-medium break-words')}>{e.emailSubject || '—'}</div>
                <div className={cn(secondaryText, 'text-[11px] truncate')} title={e.emailFrom || ''}>{e.emailFrom || '—'}</div>
                {e.platform && <div className={cn(primaryText, 'text-[11px] font-mono')}>{e.platform}</div>}
              </div>
              <div className="text-xs">
                {checkCell(e, t, true)}
                {renderRowChips(e)}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-100 dark:border-gray-700">
                {renderRowActions(e, workState)}
              </div>
            </div>
          );
        })}
      </div>
      {truncated && (
        <div className="flex justify-center pt-3">
          <button
            onClick={() => setRenderLimit((n) => n + RENDER_STEP)}
            className={cn('px-4 py-2 rounded-lg text-xs', semiBoldText,
              'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600')}
          >
            {t('adminTesting.review.loadMore', { step: Math.min(RENDER_STEP, filtered.length - visible.length), remaining: filtered.length - visible.length })}
          </button>
        </div>
      )}
      {filtered.length === 0 && (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{t('adminTesting.review.filterNoMatch')}</div>
      )}
      {detail && (
        <ReviewCardModal
          title={detail.emailSubject || detail.emailFrom || '—'}
          item={aiCheckerEmailItem(detail, t, () => { setDetail(null); onChanged?.(); })}
          onClose={() => setDetail(null)}
          t={t}
        />
      )}
      {mixedDetail && (
        <MixedCheckModal
          trace={mixedDetail.trace}
          result={mixedDetail.result}
          onClose={() => setMixedDetail(null)}
          t={t}
        />
      )}
    </div>
  );
}
